import { useMemo, useState } from "react";

// Default API base (overridable in the form for staging). The bulk tester calls
// the PUBLIC /v1/ai endpoint directly with a MANUALLY-entered key — no demo key,
// no admin session — so admins can batch-test the answers a given key produces.
const API_BASE_DEFAULT =
  (import.meta.env["VITE_API_BASE_URL"] as string | undefined) ?? "https://api.currency-core.com";

/** Categorized sample questions for bulk AI testing (topic = category). */
const SAMPLE_QUESTIONS: { category: string; questions: string[] }[] = [
  {
    category: "Simple conversion",
    questions: ["Convert 50 EUR to INR", "How much is 100 USD in AED?", "Convert 10,000 JPY to GBP"],
  },
  {
    category: "Historical conversion",
    questions: [
      "Convert 100 EUR to INR using the rate from January 1st 2020",
      "How much was 1000 AED worth in INR five years ago?",
      "Convert 50 USD to EUR using yesterday's exchange rate",
      "What was the EUR/USD rate during COVID in March 2020?",
    ],
  },
  {
    category: "Trend analysis",
    questions: [
      "How has EUR performed against USD over the last year?",
      "Has INR strengthened or weakened against USD since 2015?",
      "Show the trend of AED against INR over the last decade",
      "What is the percentage change in EUR/USD over the last 5 years?",
      "Which month saw the biggest change in EUR/USD during the last year?",
    ],
  },
  {
    category: "Comparison",
    questions: [
      "Compare EUR, GBP and AED against USD over the last 10 years",
      "Which currency performed best against INR since 2010?",
      "Compare EUR and GBP performance during the last recession",
      "Which has been more stable over the last 5 years: AED or EUR?",
      "Between EUR, GBP and JPY, which gained the most value against USD this year?",
    ],
  },
  {
    category: "Ranking",
    questions: [
      "What were the top 10 performing currencies against USD in 2025?",
      "Which currencies lost the most value against INR in the last decade?",
      "Show the strongest currencies against EUR over the last 20 years",
      "Rank GCC currencies by performance against USD since 2010",
    ],
  },
  {
    category: "Volatility, correlation & drawdown",
    questions: [
      "How volatile is EUR/USD over the last year?",
      "Which are the most stable currencies against USD?",
      "How correlated are EUR and GBP?",
      "What was EUR's worst decline against USD in the last decade?",
    ],
  },
  {
    category: "PPP queries",
    questions: [
      "How much purchasing power does $100 in the US represent in India?",
      "Convert $500 US dollars into PPP-adjusted Indian rupees",
      "Which country offers the highest purchasing power for a software engineer earning $50,000?",
      "Compare nominal exchange rate and PPP exchange rate for India",
      "Has India's PPP adjustment improved over the last decade?",
    ],
  },
  {
    category: "Multi-step reasoning",
    questions: [
      "I earn $3,000 per month in Dubai. What would that be equivalent to in Bangalore after PPP adjustment?",
      "I converted 1000 EUR to USD in 2019 and held the USD until today. Would I have gained or lost value?",
      "I'm moving from London to Bangalore. How much salary would I need to maintain the same purchasing power?",
      "Compare the purchasing power of ₹1 lakh per month in Bangalore with salaries in Dubai and London",
    ],
  },
  {
    category: "Agent workflow",
    questions: [
      "Find the strongest currency against USD over the last 10 years and summarize the reasons",
      "Compare EUR, GBP and INR over the last decade and generate a short report",
      "Identify currencies that have remained within ±5% of USD over the last 5 years",
      "Find the most volatile major currency pair in the last year",
      "Generate an investment-style summary of EUR/USD performance since 2020",
    ],
  },
];

interface RunResult {
  query: string;
  status: number;
  ok: boolean;
  /** The model's natural-language answer (or the error message). */
  answer: string;
  /** Pretty-printed full response body, for the raw toggle. */
  raw: string;
}

export function PlaygroundPage() {
  const [apiKey, setApiKey] = useState("");
  const [apiBase, setApiBase] = useState(API_BASE_DEFAULT);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [customText, setCustomText] = useState("");
  const [results, setResults] = useState<RunResult[]>([]);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const customQueries = useMemo(
    () => customText.split("\n").map((s) => s.trim()).filter(Boolean),
    [customText],
  );

  // Selected sample questions (in category order) followed by the custom ones.
  const queries = useMemo(() => {
    const picked: string[] = [];
    for (const group of SAMPLE_QUESTIONS) for (const q of group.questions) if (selected.has(q)) picked.push(q);
    return [...picked, ...customQueries];
  }, [selected, customQueries]);

  const allQuestions = useMemo(() => SAMPLE_QUESTIONS.flatMap((g) => g.questions), []);

  function toggle(q: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(q)) next.delete(q);
      else next.add(q);
      return next;
    });
  }

  function toggleCategory(group: { questions: string[] }) {
    const allOn = group.questions.every((q) => selected.has(q));
    setSelected((prev) => {
      const next = new Set(prev);
      for (const q of group.questions) {
        if (allOn) next.delete(q);
        else next.add(q);
      }
      return next;
    });
  }

  function selectAll() {
    setSelected(new Set(allQuestions));
  }
  function clearSelection() {
    setSelected(new Set());
  }

  async function runOne(q: string): Promise<RunResult> {
    const url = `${apiBase.replace(/\/+$/, "")}/v1/ai?q=${encodeURIComponent(q)}`;
    try {
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${apiKey.trim()}`, accept: "application/json" },
      });
      const text = await res.text();
      let answer = text;
      let raw = text;
      try {
        const j = JSON.parse(text) as { answer?: string; error?: { message?: string } };
        raw = JSON.stringify(j, null, 2);
        answer = j.answer ?? j.error?.message ?? text;
      } catch {
        /* non-JSON body — keep raw text */
      }
      return { query: q, status: res.status, ok: res.ok, answer, raw };
    } catch (e) {
      return { query: q, status: 0, ok: false, answer: e instanceof Error ? e.message : "Request failed", raw: "" };
    }
  }

  async function run() {
    if (!apiKey.trim()) {
      setError("Enter an API key first.");
      return;
    }
    if (queries.length === 0) {
      setError("Select at least one question or add a custom query.");
      return;
    }
    setError(null);
    setResults([]);
    setCopied(false);
    setRunning(true);
    setProgress({ done: 0, total: queries.length });
    // One by one — append each response as it returns. Sequential also paces the
    // per-minute AI rate limit naturally.
    for (let i = 0; i < queries.length; i++) {
      const r = await runOne(queries[i]!);
      setResults((prev) => [...prev, r]);
      setProgress({ done: i + 1, total: queries.length });
    }
    setRunning(false);
  }

  function copyAll() {
    const text = results
      .map((r) => `Q: ${r.query}\n${r.ok ? `A: ${r.answer}` : `ERROR ${r.status}: ${r.answer}`}`)
      .join("\n\n");
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  const selectedCount = selected.size;

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>AI Playground</h1>
          <p className="muted">
            Bulk-test the <code>/v1/ai</code> endpoint with a key you enter. Select questions and/or add custom
            ones; each runs one by one and the answers are appended below.
          </p>
        </div>
      </div>

      {error && <div className="error-banner" style={{ marginBottom: "16px" }}>{error}</div>}

      {/* Connection */}
      <div className="card card-pad" style={{ marginBottom: "16px" }}>
        <div className="form-group" style={{ marginBottom: "12px" }}>
          <label className="form-label" htmlFor="pg-key">API key (entered manually, not a demo key)</label>
          <input
            id="pg-key"
            className="form-input"
            type="password"
            autoComplete="off"
            placeholder="cc_live_…"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="pg-base">API base</label>
          <input
            id="pg-base"
            className="form-input"
            value={apiBase}
            onChange={(e) => setApiBase(e.target.value)}
            placeholder="https://api.currency-core.com"
          />
        </div>
      </div>

      {/* Question selection */}
      <div className="card card-pad" style={{ marginBottom: "16px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
          <strong style={{ fontSize: "14px" }}>Questions by topic</strong>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <span className="muted" style={{ fontSize: "12px" }}>{selectedCount} selected</span>
            <button className="btn btn-ghost btn-sm" onClick={selectAll} disabled={running}>Select all</button>
            <button className="btn btn-ghost btn-sm" onClick={clearSelection} disabled={running}>Clear</button>
          </div>
        </div>

        <div style={{ display: "grid", gap: "14px" }}>
          {SAMPLE_QUESTIONS.map((group) => {
            const allOn = group.questions.every((q) => selected.has(q));
            return (
              <div key={group.category}>
                <label style={{ display: "flex", gap: "8px", alignItems: "center", fontWeight: 600, fontSize: "12.5px", marginBottom: "6px" }}>
                  <input type="checkbox" checked={allOn} onChange={() => toggleCategory(group)} disabled={running} />
                  {group.category}
                </label>
                <div style={{ display: "grid", gap: "5px", paddingLeft: "22px" }}>
                  {group.questions.map((q) => (
                    <label key={q} style={{ display: "flex", gap: "8px", alignItems: "flex-start", fontSize: "12.5px", color: "var(--text-muted)" }}>
                      <input type="checkbox" checked={selected.has(q)} onChange={() => toggle(q)} disabled={running} style={{ marginTop: "2px" }} />
                      <span>{q}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Custom queries */}
      <div className="card card-pad" style={{ marginBottom: "16px" }}>
        <div className="form-group">
          <label className="form-label" htmlFor="pg-custom">Additional / custom queries (one per line), run on top of the selected ones</label>
          <textarea
            id="pg-custom"
            className="form-input"
            rows={4}
            placeholder={"Compare EUR and INR over the last decade\nWhat is the USD to JPY rate today?"}
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            style={{ fontFamily: "var(--font-mono, monospace)", resize: "vertical" }}
          />
        </div>
      </div>

      {/* Run */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" }}>
        <button className="btn btn-primary" onClick={run} disabled={running || queries.length === 0}>
          {running ? `Running ${progress?.done ?? 0}/${progress?.total ?? 0}…` : `Run ${queries.length} quer${queries.length === 1 ? "y" : "ies"}`}
        </button>
        {results.length > 0 && (
          <>
            <button className="btn btn-ghost" onClick={copyAll} disabled={running}>
              {copied ? "Copied ✓" : "Copy all responses"}
            </button>
            <button className="btn btn-ghost" onClick={() => setResults([])} disabled={running}>Clear results</button>
          </>
        )}
      </div>

      {/* Results — appended one by one */}
      {results.length > 0 && (
        <div style={{ display: "grid", gap: "12px" }}>
          {results.map((r, i) => (
            <div key={i} className="card card-pad">
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "10px", marginBottom: "8px" }}>
                <div style={{ fontWeight: 600, fontSize: "13px" }}>{r.query}</div>
                <span className={`badge ${r.ok ? "badge-accent" : "badge-warn"}`} style={{ flexShrink: 0 }}>
                  {r.status || "ERR"}
                </span>
              </div>
              <div style={{ fontSize: "13px", color: r.ok ? "var(--text)" : "var(--danger, crimson)", whiteSpace: "pre-wrap" }}>
                {r.answer}
              </div>
              {r.raw && (
                <details style={{ marginTop: "8px" }}>
                  <summary style={{ cursor: "pointer", fontSize: "12px", color: "var(--text-subtle)" }}>Raw response</summary>
                  <pre style={{ marginTop: "6px", fontSize: "11.5px", overflowX: "auto", background: "var(--surface-2, rgba(0,0,0,.04))", padding: "10px", borderRadius: "8px" }}>
                    {r.raw}
                  </pre>
                </details>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
